import { useState, useEffect } from 'react'
import { leadService, type DiscountLimits } from '../services/leads'
import { telephonyService } from '../../telephony/services/telephony'
import type { CallDisposition as CallDispositionType } from '../../../types'
import { CallDisposition, CallSource, CallStatus, TERMINAL_CALL_STATUSES } from '../../../types'
import { DISPOSITION_LABELS } from '../../../lib/constants'
import { debugLog } from '../../../lib/debugLog'
import { AlertCircle, X, Phone, Bell } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { toLocalInputValue } from '../../../lib/datetime'

type DispositionPayload = Parameters<typeof leadService.updateDisposition>[1]

interface CallDispositionModalProps {
  isOpen: boolean
  /** Exotel call log ID — null when logging a manual call (Smart Connect OFF). */
  callLogId: number | null
  /** Required when callLogId is null — used to POST to /leads/{leadId}/calls/log. */
  leadId?: number
  /** Call source for manual modes. Defaults to MANUAL_DESKTOP when callLogId is null. */
  source?: CallSource
  customerName: string
  callStatus: CallStatus | null
  /** Set once the caller gives up polling for a terminal status (e.g. after a
   *  fixed timeout) — the modal is unlocked for manual disposition entry even
   *  though the call was never confirmed connected/ended. */
  pollTimedOut?: boolean
  /** True when the OEM ambient scan has found and uploaded the recording for this call. */
  recordingFound?: boolean
  onClose: () => void
  onSaved: () => void
  /** Optional override — if provided, called instead of the direct API call.
   *  Allows callers to inject an offline-aware submission path. */
  onSubmitOverride?: (callLogId: number, payload: DispositionPayload) => Promise<void>
}

const CALL_STATUS_PILL: Record<string, { label: string; color: string; bg: string; pulse?: boolean }> = {
  [CallStatus.INITIATED]:   { label: 'Ringing…',   color: 'var(--warning-text)', bg: 'var(--warning-bg)', pulse: true },
  [CallStatus.IN_PROGRESS]: { label: 'Connected',   color: 'var(--success-text)', bg: 'var(--success-bg)' },
  [CallStatus.COMPLETED]:   { label: 'Call ended',  color: 'var(--text-tertiary)', bg: 'var(--surface-subtle)' },
  [CallStatus.NO_ANSWER]:   { label: 'No answer',   color: 'var(--text-tertiary)', bg: 'var(--surface-subtle)' },
  [CallStatus.BUSY]:        { label: 'Line busy',   color: 'var(--danger-text)', bg: 'var(--danger-bg)' },
  [CallStatus.FAILED]:      { label: 'Call failed', color: 'var(--danger-text)', bg: 'var(--danger-bg)' },
}

export default function CallDispositionModal({
  isOpen,
  callLogId,
  leadId,
  source,
  customerName,
  callStatus,
  pollTimedOut = false,
  recordingFound = false,
  onClose,
  onSaved,
  onSubmitOverride,
}: CallDispositionModalProps) {
  const [disposition, setDisposition] = useState<CallDispositionType>(CallDisposition.NO_ANSWER)
  const [remarks, setRemarks]         = useState('')
  const [callbackAt, setCallbackAt]   = useState('')
  const [odDiscount, setOdDiscount]   = useState('')
  const [submitting, setSubmitting]   = useState(false)
  const [error, setError]             = useState('')
  const [limits, setLimits]           = useState<DiscountLimits>({ telecaller: 0, team_leader: 15, insurance_head: 30 })

  // ── Post-save reminder nudge (NO_ANSWER only — CALLBACK_REQUESTED already
  //    requires a callback time above) — see docs/adr-reminder-compliance.md ──
  const [reminderPrompt, setReminderPrompt] = useState<{ callLogId: number; leadId: number } | null>(null)
  const [promptChoice, setPromptChoice]     = useState<'ask' | 'decline'>('ask')
  const [promptReminderAt, setPromptReminderAt] = useState('')
  const [declineReason, setDeclineReason]   = useState('')
  const [promptSubmitting, setPromptSubmitting] = useState(false)
  const [promptError, setPromptError]       = useState('')

  useEffect(() => {
    if (!isOpen) return
    leadService.getDiscountLimits().then(setLimits).catch(() => {})
  }, [isOpen])

  if (!isOpen) return null

  // Manual mode: callLogId is null, leadId required
  const isManual = callLogId === null
  if (isManual && !leadId) return null

  const isTerminal = isManual || pollTimedOut || (callStatus !== null && TERMINAL_CALL_STATUSES.has(callStatus))
  const pill = callStatus ? CALL_STATUS_PILL[callStatus] : null

  const discountPct = parseFloat(odDiscount) || 0
  const discountHint = (() => {
    if (!odDiscount || discountPct <= 0) return null
    if (discountPct <= limits.telecaller)
      return { text: `Within your self-approval limit (${limits.telecaller}%)`, color: 'var(--success-text)' }
    if (discountPct <= limits.team_leader)
      return { text: `Exceeds your limit — will be sent to Team Leader for approval (limit ${limits.team_leader}%)`, color: 'var(--warning-text)' }
    if (discountPct <= limits.insurance_head)
      return { text: `Exceeds Team Leader limit — will be sent to Insurance Head for approval (limit ${limits.insurance_head}%)`, color: 'var(--warning-text)' }
    return { text: `Exceeds Insurance Head limit (${limits.insurance_head}%) — will still be escalated`, color: 'var(--danger-text)' }
  })()

  const handleSubmit = async () => {
    setError('')

    if (disposition === CallDisposition.CALLBACK_REQUESTED && !callbackAt) {
      setError('Please select a callback time')
      return
    }

    setSubmitting(true)
    try {
      const payload: Record<string, unknown> = { disposition, remarks }
      if (callbackAt) payload.callback_at = new Date(callbackAt).toISOString()
      if (odDiscount) {
        const pct = parseFloat(odDiscount)
        payload.od_discount_offered = pct
        payload.requires_approval = pct > limits.telecaller
      }

      let resolvedCallLogId: number | null = callLogId
      let reminderRecommended = false
      // Offline-queued submissions (onSubmitOverride) don't get a server
      // response back, so there's no reliable call_log_id / flag to prompt
      // with — the reminder nudge only runs on the direct, online paths.
      const canPrompt = !onSubmitOverride

      if (isManual && leadId) {
        const res = await leadService.logManualCall(leadId, {
          source: source ?? CallSource.MANUAL_DESKTOP,
          disposition,
          remarks: remarks || undefined,
          callback_at: callbackAt ? new Date(callbackAt).toISOString() : undefined,
          od_discount_offered: odDiscount ? parseFloat(odDiscount) : undefined,
          requires_approval: odDiscount ? parseFloat(odDiscount) > limits.telecaller : false,
        })
        resolvedCallLogId = res.call_log_id
        reminderRecommended = !!res.reminder_recommended
      } else {
        const typedPayload = payload as DispositionPayload
        if (onSubmitOverride) {
          await onSubmitOverride(callLogId!, typedPayload)
        } else {
          const res = await leadService.updateDisposition(callLogId!, typedPayload)
          reminderRecommended = !!res.reminder_recommended
        }
      }

      setDisposition(CallDisposition.NO_ANSWER)
      setRemarks('')
      setCallbackAt('')
      setOdDiscount('')
      setError('')

      if (canPrompt && reminderRecommended && leadId && resolvedCallLogId) {
        setReminderPrompt({ callLogId: resolvedCallLogId, leadId })
        setPromptChoice('ask')
        const d = new Date(Date.now() + 60 * 60 * 1000)
        d.setSeconds(0, 0)
        setPromptReminderAt(toLocalInputValue(d))
        setDeclineReason('')
        setPromptError('')
      } else {
        onSaved()
      }
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      const reason = err instanceof Error ? err.message : ''
      debugLog('Disposition', 'save failed', { detail, reason })
      setError(detail || (reason ? `Failed to save disposition (${reason})` : 'Failed to save disposition'))
    } finally {
      setSubmitting(false)
    }
  }

  const handlePromptSetReminder = async () => {
    if (!reminderPrompt || !promptReminderAt) return
    setPromptSubmitting(true)
    setPromptError('')
    try {
      await telephonyService.createReminder({
        lead_id: reminderPrompt.leadId,
        reminder_at: new Date(promptReminderAt).toISOString(),
        source: 'SYSTEM_PROMPTED',
      })
      setReminderPrompt(null)
      onSaved()
    } catch {
      setPromptError('Failed to set reminder')
    } finally {
      setPromptSubmitting(false)
    }
  }

  const handlePromptDecline = async () => {
    if (!reminderPrompt || !declineReason.trim()) {
      setPromptError('Please tell us why — this is tracked for follow-up compliance')
      return
    }
    setPromptSubmitting(true)
    setPromptError('')
    try {
      await telephonyService.declineReminder(reminderPrompt.callLogId, declineReason.trim())
      setReminderPrompt(null)
      onSaved()
    } catch {
      setPromptError('Failed to record — please try again')
    } finally {
      setPromptSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!submitting) {
      setError('')
      onClose()
    }
  }

  if (reminderPrompt) {
    return (
      <ModalOverlay onClose={() => {}}>
        <div className="modal-panel" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="flex items-center gap-2.5">
              <Bell className="h-4 w-4" style={{ color: 'var(--brand-600)' }} />
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                Set a follow-up reminder?
              </h2>
            </div>
          </div>
          <p className="text-sm mb-3" style={{ color: 'var(--text-tertiary)' }}>
            This disposition usually needs a callback. When would you like to be reminded?
          </p>

          {promptError && (
            <div className="alert-danger mb-3">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{promptError}</span>
            </div>
          )}

          {promptChoice === 'ask' && (
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">Reminder time</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={promptReminderAt}
                  onChange={e => setPromptReminderAt(e.target.value)}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="btn-secondary"
                  onClick={() => setPromptChoice('decline')}
                  disabled={promptSubmitting}
                >
                  Skip
                </button>
                <button
                  className="btn-primary"
                  onClick={handlePromptSetReminder}
                  disabled={promptSubmitting || !promptReminderAt}
                >
                  {promptSubmitting ? 'Saving…' : 'Set Reminder'}
                </button>
              </div>
            </div>
          )}

          {promptChoice === 'decline' && (
            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">
                  Why skip the reminder? <span style={{ color: 'var(--danger-text)' }}>*</span>
                </label>
                <input
                  type="text"
                  className="input"
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  placeholder="e.g. Already spoke to customer separately"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="btn-secondary"
                  onClick={() => { setPromptChoice('ask'); setPromptError('') }}
                  disabled={promptSubmitting}
                >
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={handlePromptDecline}
                  disabled={promptSubmitting}
                >
                  {promptSubmitting ? 'Saving…' : 'Confirm Skip'}
                </button>
              </div>
            </div>
          )}
        </div>
      </ModalOverlay>
    )
  }

  return (
    <ModalOverlay onClose={handleClose}>
      <div className="modal-panel-flex" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-flex-header">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                Log Call — {customerName}
              </h2>
              {!isManual && pill && (
                <span
                  className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full${pill.pulse ? ' animate-pulse' : ''}`}
                  style={{ color: pill.color, background: pill.bg }}
                >
                  <Phone className="h-3 w-3" />
                  {pill.label}
                </span>
              )}
              {isManual && (
                <span
                  className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ color: 'var(--text-tertiary)', background: 'var(--surface-subtle)' }}
                >
                  Manual
                </span>
              )}
            </div>
            {!isManual && !isTerminal && callStatus !== null && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                Waiting for call to end before you can save…
              </p>
            )}
            {!isManual && pollTimedOut && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--warning-text)' }}>
                Couldn't confirm the call ended — you can log the outcome manually now.
              </p>
            )}
            {(isManual || (isTerminal && !pollTimedOut)) && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                {isManual ? 'Log the outcome of the call you made.' : 'Record the outcome of the call.'}
              </p>
            )}
            {recordingFound && (
              <p className="text-xs mt-1 inline-flex items-center gap-1 font-medium"
                style={{ color: 'var(--success-text)' }}>
                ● Recording found — AI analysis in progress
              </p>
            )}
          </div>
          <button className="btn-icon" onClick={handleClose} disabled={submitting}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="modal-flex-body space-y-4 pt-0">
        {error && (
          <div className="alert-danger mb-1">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Disposition <span style={{ color: 'var(--danger-text)' }}>*</span>
            </label>
            <select
              className="input"
              value={disposition}
              onChange={e => setDisposition(e.target.value as CallDispositionType)}
              disabled={!isTerminal}
            >
              {Object.entries(DISPOSITION_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {disposition === CallDisposition.CALLBACK_REQUESTED && (
            <div className="form-group">
              <label className="form-label">
                Callback Time <span style={{ color: 'var(--danger-text)' }}>*</span>
              </label>
              <input
                type="datetime-local"
                className="input"
                value={callbackAt}
                onChange={e => setCallbackAt(e.target.value)}
                required
                disabled={!isTerminal}
              />
            </div>
          )}

          {disposition === CallDisposition.ASKING_FOR_DISCOUNT && (
            <div className="form-group">
              <label className="form-label">
                OD Discount Offered (%)
                {limits.telecaller > 0 && (
                  <span className="ml-1 font-normal" style={{ color: 'var(--text-tertiary)' }}>
                    — you can self-approve up to {limits.telecaller}%
                  </span>
                )}
              </label>
              <input
                type="number"
                className="input"
                value={odDiscount}
                onChange={e => setOdDiscount(e.target.value)}
                min="0"
                max="100"
                step="0.1"
                placeholder="e.g. 10"
                disabled={!isTerminal}
              />
              {discountHint && (
                <p className="form-hint flex items-center gap-1 mt-1" style={{ color: discountHint.color }}>
                  ⚠ {discountHint.text}
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Remarks</label>
            <textarea
              className="input"
              rows={3}
              maxLength={500}
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder="Call notes…"
            />
            <p className="form-hint">{remarks.length}/500</p>
          </div>
        </div>
        </div>

        <div className="modal-flex-footer">
          <button className="btn-secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !isTerminal}
            title={!isTerminal ? 'Waiting for call to end…' : undefined}
          >
            {submitting ? 'Saving…' : !isTerminal ? 'Waiting for call…' : 'Save Disposition'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}
